import { useCallback, useState } from "react";

import Modal from "../../components/Modal";
import EntryForm from "./EntryForm";
import type {
  CarSummary,
  FormOptionsResponse,
  FuelStation,
} from "../../api/types";

export interface NewEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  formOptions: FormOptionsResponse;
  cars: CarSummary[];
  activeCarId: number | null;
  fuelStations: FuelStation[];
  recentFuelStationId: number | null;
}

/**
 * Modal-shaped wrapper around `<EntryForm>`. Owns the dirty-confirm logic:
 * if the user has typed anything and tries to close, they get the
 * "Discard this entry?" prompt — same wording as the legacy `new-entry.js`.
 *
 * On a successful save, the form calls `onSaved` which closes the modal
 * unconditionally (no confirm — the entry was committed).
 */
export default function NewEntryModal({
  isOpen,
  onClose,
  formOptions,
  cars,
  activeCarId,
  fuelStations,
  recentFuelStationId,
}: NewEntryModalProps) {
  const [isDirty, setIsDirty] = useState(false);

  const requestClose = useCallback(() => {
    if (isDirty) {
      const ok = window.confirm(
        "Discard this entry? The values you entered will be lost.",
      );
      if (!ok) return;
    }
    setIsDirty(false);
    onClose();
  }, [isDirty, onClose]);

  const handleSaved = useCallback(() => {
    setIsDirty(false);
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={requestClose} title="New entry">
      {isOpen ? (
        <EntryForm
          formOptions={formOptions}
          cars={cars}
          activeCarId={activeCarId}
          fuelStations={fuelStations}
          recentFuelStationId={recentFuelStationId}
          onDirtyChange={setIsDirty}
          onSaved={handleSaved}
        />
      ) : null}
    </Modal>
  );
}
